import type { MapElements } from '../../utilities/MapElements';
import { Warehouse } from './Warehouse';
import { WarehouseApi } from './WarehouseApi';
import { get } from 'svelte/store';
import { api } from '../../../stores/api';

export class WarehousesElements {
    private warehouses: Warehouse[] = [];
    private mapIds: Map<string, string> = new Map(); // Map { mapElementId: warehouse.id }

    public setWarehouses(warehouses: Warehouse[]) {
        this.warehouses = warehouses;
    }

    public getWarehouses() {
        return this.warehouses;
    }

    public drawWarehouses() {
        if (typeof(Targeo) == 'object') {
            console.table(this.mapIds);
            this.clearMap();
            Targeo.Base.Map.getObjectManager().disableRefreshing('LastMile:WarehousesElements');
            this.warehouses.forEach(warehouse => {
                this._drawWarehouse(warehouse);
                console.log('warehousesElements, drawing warehouse', warehouse.getX(), warehouse.getY(), warehouse.getAbbreviation());
            });
            Targeo.Base.Map.getObjectManager().enableRefreshing('LastMile:WarehousesElements');
        }
    }

    private _drawWarehouse(warehouse: Warehouse) {
        if (warehouse.getX() && warehouse.getY()) {
            let mapObject = new Targeo.MapObject.Label(
                {
                    x: warehouse.getX(),
                    y: warehouse.getY(),
                },
                warehouse.getAbbreviation() || '?',
                {
                    fs: 15,
                    fc: '#000000',
                    fm: { b: true },
                    bw: 1,
                    bc: '#000000',
                    bs: 'solid',
                    br: 4,
                    p: [1, 3, 1, 3],
                    bgc: '#98c8f8',
                }
            );
            Targeo.Base.Map.UOAdd(mapObject);
            this.mapIds.set(mapObject.id, warehouse.innerId);
        }
    }

    public clearMap() {
        // get keys from mapIds
        Targeo.Base.Map.getObjectManager().disableRefreshing('LastMile:WarehousesElements');
        this.mapIds.forEach((value, id) => {
            console.log('warehousesElements, removing map element', id);
            Targeo.Base.Map.UORemove(id);
        });
        Targeo.Base.Map.getObjectManager().enableRefreshing('LastMile:WarehousesElements');
        this.mapIds.clear();
    }

    public zoomToAllWarehouses() {
        let bounds = {
            minX: 0,
            minY: 0,
            maxX: 0,
            maxY: 0,
        };

        this.warehouses.forEach(warehouse => {
            if (warehouse.getX() !== null && warehouse.getY() !== null) {
                if (bounds.minX === 0 || bounds.minX > warehouse.getX()) {
                    bounds.minX = warehouse.getX();
                }
                if (bounds.minY === 0 || bounds.minY > warehouse.getY()) {
                    bounds.minY = warehouse.getY();
                }
                if (bounds.maxX === 0 || bounds.maxX < warehouse.getX()) {
                    bounds.maxX = warehouse.getX();
                }
                if (bounds.maxY === 0 || bounds.maxY < warehouse.getY()) {
                    bounds.maxY = warehouse.getY();
                }
            }
        });

        if (
            bounds &&
            this.warehouses.length > 0 &&
            bounds.minX !== 0 &&
            bounds.minY !== 0 &&
            bounds.maxX !== 0 &&
            bounds.maxY !== 0
        ) {
            // make bounds bigger by 10%
            let dx = bounds.maxX - bounds.minX;
            let dy = bounds.maxY - bounds.minY;
            bounds.minX -= dx * 0.1;
            bounds.maxX += dx * 0.1;
            bounds.minY -= dy * 0.1;
            bounds.maxY += dy * 0.1;
            Targeo.Base.Map.zoomToBounds(bounds);
        }
    }

    public count() {
        return this.warehouses.length;
    }

    public getWarehouseByInnerId(innerId: string): Warehouse | null {
        return this.warehouses.find(warehouse => warehouse.innerId === innerId) || null;
    }

    public filter(query: string) {
        return this.warehouses.filter(warehouse => {
            return (
                warehouse.getName().toLowerCase().includes(query.toLowerCase()) ||
                warehouse.getAbbreviation().toLowerCase().includes(query.toLowerCase()) ||
                warehouse.getAddress().toLowerCase().includes(query.toLowerCase())
            );
        });
    }

    async save(warehouse: Warehouse) {
        let id = warehouse.getId();
        warehouse.calcAbbreviation(this.warehouses);
        let data = warehouse.get();
        let error: string = '';
        if (data === null) {
            console.error('Warehouse data is null');
            throw new Error('Warehouse data is null');
        }
        console.log('Warehouse data', data, id);
        if (id === null) {
            let w = await WarehouseApi.createOne(get(api), data).catch(e => {
                error = e.message;
                throw new Error(e.message);
            });
            if (!error && w) {
                warehouse.set(w, w.id);
                this.warehouses.push(warehouse);
                console.log('Warehouse created', warehouse.get(), warehouse.getId());
            }
            console.log('WarehouseApi.createOne', w, error);
        } else {
            let wr = await WarehouseApi.updateOne(get(api), id, data).catch(e => {
                error = e.message;
                throw new Error(e.message);
            });
            if (wr) {
                this.warehouses = this.warehouses.map(w => {
                    if (w.innerId === warehouse.innerId) {
                        return warehouse;
                    }
                    return w;
                });
            }
        }
    }

    remove(warehouse: Warehouse) {
        // get mapElementId from mapIds where value is warehouse.innerId
        let mapElementId = Array.from(this.mapIds.entries()).find(([key, value]) => value === warehouse.innerId)?.[0];
        if (mapElementId) {
            Targeo.Base.Map.UORemove(mapElementId);
            this.mapIds.delete(mapElementId);
        }
        this.warehouses = this.warehouses.filter(w => w.innerId !== warehouse.innerId);
    }
}
